// components/BudgetPanel/BreakdownSelector.tsx
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.svg';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';

interface Props {
  onChange: (value: 'section' | 'timePeriod' | string) => void; // string for filter IDs
  value: 'section' | 'timePeriod' | string;
}

export const BreakdownSelector: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { sortableFilters, activeFilterIds } = useEditorContext();

  // Get active filters that can be used for breakdown
  // Use sortableFilters instead of filters to respect the reordered state
  const activeFilters = sortableFilters
    .filter(f => f.isVisible && activeFilterIds.includes(f.id!))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const baseOptions = [
    { value: 'timePeriod' as const, label: t('editor.sidebar.initiative.budget.time') },
    { value: 'section' as const, label: t('editor.sidebar.initiative.budget.sunray') },
  ];

  // Add active filters as breakdown options
  const filterOptions = activeFilters.map(filter => ({
    value: `filter-${filter.id}`,
    label: filter.title || 'Filter',
  }));

  const options = [...baseOptions, ...filterOptions];

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (value.startsWith('filter-')) {
      const filterId = parseInt(value.replace('filter-', ''));
      if (!activeFilterIds.includes(filterId)) {
        onChange('timePeriod');
      }
    }
  }, [activeFilterIds, value, onChange]);

  return (
    <Dropdown ref={dropdownRef}>
      <DropdownButton onClick={() => setIsOpen(!isOpen)}>
        <Label>{selectedOption.label}</Label>
        <ArrowIcon $isOpen={isOpen} />
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          {options.map(option => (
            <DropdownItem
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              $isSelected={option.value === value}
            >
              {option.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </Dropdown>
  );
};

const Dropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  min-width: 78px;
`;

const Label = styled.span`
  font-weight: 500;
`;

const ArrowIcon = styled(ArrowDownIcon)<{ $isOpen: boolean }>`
  width: 0.75rem;
  height: 0.75rem;
  transition: transform 0.2s;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 125px;
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: 0.75rem;
  cursor: pointer;
  font-size: 1rem;
  background: ${({ $isSelected }) => ($isSelected ? '#f5f5f5' : 'transparent')};

  &:hover {
    background: #f5f5f5;
  }

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
`;
